import React from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';

interface NavigationBarProps {
  activeTab: number;
  setActiveTab: (index: number) => void;
  isConfigValid: () => string;
  isSaving: boolean;
  handleSave: () => void;
  isProcessing: boolean;
  inputFilePath: string;
  isReadyToProcess: () => boolean | "";
  handleStartBatchProcess: () => void;
  selectedModel: string;
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  activeTab,
  setActiveTab,
  isConfigValid,
  isSaving,
  handleSave,
  isProcessing,
  inputFilePath,
  isReadyToProcess,
  handleStartBatchProcess,
  selectedModel
}) => {

    const getIsReadyToProcess = () => {
        if (typeof isReadyToProcess === 'function') {
            return isReadyToProcess();
        }
        return isReadyToProcess;
    };

    const getIsConfigValid = () => { 
        if (typeof isConfigValid === 'function') {
            return isConfigValid();
        }
        return isConfigValid;
    }

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        position: 'sticky',
        bottom: 0,
        mt: 3,
        pt: 2,
        pb: 2,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        zIndex: 1
      }}
    >
      {/* Left side - Previous button */}
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
        disabled={activeTab === 0}
      >
        Previous
      </Button>
      
      {/* Center - Indicator message */}
      <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: 8 }}>
        {!getIsConfigValid() && 
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ opacity: 0.8 }}
          >
            Configuration ready to save
          </Typography>
        }
      </Box>

      {/* Right side - Save and Next/Process buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Save Configuration button with tooltip */}
        <Tooltip title="Save your configuration before proceeding or leaving">
          <span> {/* Wrapper to ensure tooltip works when button is disabled */}
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={isSaving || !getIsConfigValid()}
            >
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </span>
        </Tooltip>

        {/* Conditionally show Start Batch Process button on the last tab with tooltip */}
        {activeTab === 4 && (
          <Tooltip title={`Start a batch job using the ${selectedModel} model to process your keywords according to this configuration.`}>
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartBatchProcess}
              disabled={isProcessing || !inputFilePath || !getIsReadyToProcess()}
            >
              {isProcessing ? 'Processing...' : 'Start Batch Process'}
            </Button>
          </Tooltip>
        )}

        {/* Only show Next button if not on last tab - with consistent primary color */}
        {activeTab < 4 && (
          <Button
            variant="contained"
            endIcon={<PlayArrowIcon />}
            color="primary"
            onClick={() => {              

              if (!getIsConfigValid() && !isSaving) {
                handleSave();
              }
              setActiveTab(Math.min(4, activeTab + 1));
            }}
          >
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default NavigationBar;
